<script setup lang="ts">
import { ROUTES } from '@configs'
import { NovaModalDialog } from '#components'
import type { ModalDaoRejectDialogParams } from '@components/NovaModalDaoRejectDialog/NovaModalDaoRejectDialog.types'

const { t } = useI18n()
const { showSignInDialog } = useMembershipProcess()
const { show: modalShow, hide: modalHide } = useModal()

const layoutStore = useLayoutStore()
const daoStore = useDaoStore()
const userStore = useUserStore()
const commStore = useCommStore()
const route = useRoute()

definePageMeta({
  layout: 'one-column',
})

const daoApplyButtonDisabled = computed(() => {
  if (
    daoStore.daoStatus?.daoSttusCode === 'P' ||
    daoStore.daoStatus?.daoSttusCode === 'D' ||
    daoStore.daoStatus?.daoSttusCode === 'T30' ||
    daoStore.daoStatus?.daoSttusCode === 'L'
  ) {
    return true
  }
  return false
})
const daoApplyBtnLabel = computed(() => {
  // P(제안신청 proposal) R(불허 Reject) A(Activate) T(해산) 해산신청(D) L(Lock/동결)
  const status = daoStore.daoStatus?.daoSttusCode
  switch (status) {
    case 'P':
      return t('dao.accession.applyModel.btnBox.pending')
    case 'A':
      return t('dao.accession.applyModel.btnBox.participating')
    case 'D':
    case 'L':
    case 'T':
    case 'R':
    default:
      return t('dao.accession.applyModel.btnBox.applyBtn')
  }
})

const goDaoApply = async () => {
  // 로그인 체크
  if (!userStore.isSignIn) {
    await showSignInDialog()
    return
  }
  // 크리에이터 여부 체크
  if (!userStore.isCreator) {
    await modalShow({
      component: NovaModalDialog,
      bind: {
        name: modalsName.MODAL_DIALOG,
        btns: [
          {
            label: t('dao.opening.dialog.btnBox.cancelBtn'),
            theme: 'secondary-gray-200',
            size: 32,
            onClick: async () => {
              await modalHide(modalsName.MODAL_DIALOG)
            },
          },
          {
            label: t('dao.opening.dialog.btnBox.applyBtn'),
            theme: 'primary-blue-dark',
            size: 32,
            onClick: async () => {
              await modalHide(modalsName.MODAL_DIALOG)
              await useNavigations({
                url: ROUTES.APPLY_CREATOR.path,
              })
            },
          },
        ],
      },
      slots: {
        title: t('dao.opening.dialog.title'),
        content: t('dao.opening.dialog.creatorMessage'),
      },
    })
    return
  }
  // 참여중 일때는 DAO 홈으로 이동
  if (daoStore.daoStatus?.daoSttusCode === 'A') {
    let url = await commStore.fetchHomeUrl({
      userSn: userStore.user?.userSn || 0,
    })
    url = `${url}/dao`
    await useNavigations({ url })
    return
  }
  // 해산 여부 체크
  if (daoStore.daoStatus?.daoSttusCode === 'T30') {
    await modalShow({
      component: NovaModalDialog,
      bind: {
        name: modalsName.MODAL_DIALOG,
        btns: [
          {
            label: t('dao.opening.dialog.btnBox.checkBtn'),
            theme: 'primary-blue-dark',
            size: 32,
            onClick: async () => {
              await modalHide(modalsName.MODAL_DIALOG)
            },
          },
        ],
      },
      slots: {
        title: t('dao.opening.dialog.title'),
        content: t('dao.opening.dialog.dissolutionMessage'),
      },
    })
  }
  // 팔로우 수 체크
  if (!daoStore.daoOpeningAvailable?.openingAvailable) {
    await modalShow({
      component: NovaModalDialog,
      bind: {
        name: modalsName.MODAL_DIALOG,
        btns: [
          {
            label: t('dao.opening.dialog.btnBox.checkBtn'),
            theme: 'primary-blue-dark',
            size: 32,
            onClick: async () => {
              await modalHide(modalsName.MODAL_DIALOG)
            },
          },
        ],
      },
      slots: {
        title: t('dao.opening.dialog.title'),
        content: t('dao.opening.dialog.followMessage'),
      },
    })
    return
  }
  // 신청페이지로 이동
  await useNavigations({
    url: ROUTES.APPLY_DAO_FORM.path,
  })
}

const goDaoCreatorList = () => {
  useNavigations({
    url: ROUTES.CREATORS_ALL.path,
    query: { ...route.query, tab: 'dao' },
  })
}

onMounted(async () => {
  layoutStore.updateMainBgWhite(true)
  if (userStore.isSignIn) {
    await daoStore.fetchDaoStatus()
    await daoStore.fetchDaoOpeningAvailable()
    // 반려 됐을 때
    if (daoStore.daoOpeningAvailable?.daoCrtfcResultCn) {
      await modalShow(modalsName.MODAL_DAO_REJECT_DIALOG, {
        daoCrtfcResultCn: daoStore.daoOpeningAvailable?.daoCrtfcResultCn,
      } as ModalDaoRejectDialogParams)
      return
    }
    if (daoStore.daoStatus?.daoSttusCode === 'A') {
      await modalShow({
        component: NovaModalDialog,
        bind: {
          name: modalsName.MODAL_DIALOG,
          btns: [
            {
              label: t('dao.opening.dialog.btnBox.checkBtn'),
              theme: 'primary-blue-dark',
              size: 32,
              onClick: async () => {
                await modalHide(modalsName.MODAL_DIALOG)
              },
            },
          ],
        },
        slots: {
          title: t('dao.approve.dialog.title'),
          content: t('dao.approve.dialog.approvalMessage'),
        },
      })
    }
  }
})

onBeforeUnmount(() => {
  layoutStore.updateMainBgWhite(false)
})
</script>

<template>
  <div class="dao-index">
    <div class="dao-item01">
      <div class="text-box">
        <p
          v-dompurify-html="t(`dao.index.daoItem01.textBox.text01`)"
          class="text sm"
        />
        <p
          v-dompurify-html="t(`dao.index.daoItem01.textBox.text02`)"
          class="text lg"
        />
      </div>
      <div class="btn-box">
        <NovaButtonText
          class="top-button"
          :label="t('dao.index.daoItem01.btnBox.listOfDAO')"
          :theme="'transparent-text-white'"
          @click="goDaoCreatorList"
        />
        <NovaButtonText
          class="top-button"
          :label="daoApplyBtnLabel"
          :theme="'white-border-gray'"
          :disabled="daoApplyButtonDisabled"
          @click="goDaoApply"
        />
        <!--        <NovaButtonText
          class="top-button"
          :label="t('dao.index.daoItem01.btnBox.reviewing')"
          :theme="'disable-gray'"
        />-->
      </div>
    </div>
    <div class="dao-item02">
      <div class="text-box">
        <p
          v-dompurify-html="t(`dao.index.daoItem02.textBox.title`)"
          class="title"
        />
        <p
          v-dompurify-html="t(`dao.index.daoItem02.textBox.message`)"
          class="message"
        />
      </div>
      <div class="governance-box">
        <p
          v-dompurify-html="t(`dao.index.daoItem02.governanceBox.title`)"
          class="title"
        />
        <ul class="governance-step">
          <li>
            <p
              v-dompurify-html="t(`dao.index.daoItem02.governanceBox.nft`)"
              class="step nft"
            />
          </li>
          <li class="arrow">
            <img src="@/assets/images/dao/icon-arrow.svg?url" alt="step" />
          </li>
          <li>
            <p
              v-dompurify-html="t(`dao.index.daoItem02.governanceBox.voting`)"
              class="step voting"
            />
          </li>
          <li class="arrow">
            <img src="@/assets/images/dao/icon-arrow.svg?url" alt="step" />
          </li>
          <li>
            <p
              v-dompurify-html="
                t(`dao.index.daoItem02.governanceBox.community`)
              "
              class="step community"
            />
          </li>
        </ul>
      </div>
    </div>
    <div class="dao-item03">
      <p v-dompurify-html="t(`dao.index.daoItem03.title`)" class="title" />
      <dl class="creator">
        <dt
          v-dompurify-html="t(`dao.index.daoItem03.daoCreator.title`)"
          class="title"
        />
        <dd>
          <ul class="box-item">
            <li>
              <div class="dd-cont">
                <dl>
                  <dt
                    v-dompurify-html="
                      t(`dao.index.daoItem03.daoCreator.creative.title`)
                    "
                  />
                  <dd
                    v-dompurify-html="
                      t(`dao.index.daoItem03.daoCreator.creative.message`)
                    "
                    class="creative"
                  />
                </dl>
              </div>
            </li>
            <li>
              <div class="dd-cont">
                <dl>
                  <dt
                    v-dompurify-html="
                      t(`dao.index.daoItem03.daoCreator.interaction.title`)
                    "
                  />
                  <dd
                    v-dompurify-html="
                      t(`dao.index.daoItem03.daoCreator.interaction.message`)
                    "
                    class="interaction"
                  />
                </dl>
              </div>
            </li>
          </ul>
        </dd>
      </dl>
      <dl class="member">
        <dt
          v-dompurify-html="t(`dao.index.daoItem03.daoMember.title`)"
          class="title"
        />
        <dd>
          <ul class="box-item">
            <li>
              <div class="dd-cont">
                <dl>
                  <dt
                    v-dompurify-html="
                      t(`dao.index.daoItem03.daoMember.cooperation.title`)
                    "
                  />
                  <dd
                    v-dompurify-html="
                      t(`dao.index.daoItem03.daoMember.cooperation.message`)
                    "
                    class="cooperation"
                  />
                </dl>
              </div>
            </li>
            <li>
              <div class="dd-cont">
                <dl>
                  <dt
                    v-dompurify-html="
                      t(`dao.index.daoItem03.daoMember.communication.title`)
                    "
                  />
                  <dd
                    v-dompurify-html="
                      t(`dao.index.daoItem03.daoMember.communication.message`)
                    "
                    class="communication"
                  />
                </dl>
              </div>
            </li>
          </ul>
        </dd>
      </dl>
    </div>
    <div class="dao-item04">
      <p v-dompurify-html="t(`dao.index.daoItem04.title`)" class="title" />
      <ul class="textBox">
        <li>
          <dl class="creation">
            <dt
              v-dompurify-html="t(`dao.index.daoItem04.textBox.creation.title`)"
            />
            <dd
              v-dompurify-html="
                t(`dao.index.daoItem04.textBox.creation.message`)
              "
            />
          </dl>
        </li>
        <li>
          <dl class="participation">
            <dt
              v-dompurify-html="
                t(`dao.index.daoItem04.textBox.participation.title`)
              "
            />
            <dd
              v-dompurify-html="
                t(`dao.index.daoItem04.textBox.participation.message`)
              "
            />
          </dl>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped lang="scss">
.dao-index {
  background-color: $color-black;
  color: #fff;
  text-align: center;
  .dao-item01 {
    position: relative;
    padding: 160px 20px 110px 20px;
    background: linear-gradient(
      121.15deg,
      rgba(28, 24, 84, 0.5) 6.04%,
      rgba(8, 20, 64, 0.5) 95.29%
    );
    transition: all 0.3s ease;
    @include mobile {
      padding: 80px 20px 60px 20px;
    }
    &:before {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      right: 0;
      width: 100%;
      max-width: 621px;
      height: 545px;
      background: url('@/assets/images/dao/circle01.svg?url') no-repeat center
        top;
      background-size: 100% auto;
    }

    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      max-width: 664px;
      height: 482px;
      bottom: 0;
      left: 0;
      background: url('@/assets/images/dao/circle02.svg?url') no-repeat center
        bottom;
      background-size: 100% auto;
    }
    .text-box {
      position: relative;
      z-index: 1;
      .text {
        &.sm {
          font-weight: 400;
          font-size: 30px;
          @include mobile {
            @include text-style($text-display-regular);
          }
        }
        &.lg {
          font-size: 72px;
          font-weight: 700;
          line-height: 1.1em;
          @include mobile {
            @include text-style($text-custom-35-bold);
          }
        }
      }
    }
    .btn-box {
      position: relative;
      z-index: 1;
      display: flex;
      flex-direction: column;
      gap: 24px;
      padding-top: 50px;
      @include mobile {
        padding-top: 40px;
      }
      .top-button {
        width: 100%;
        max-width: 230px;
        margin: 0 auto;
        border-radius: 20px;
        @include mobile {
          max-width: 180px;
        }
      }
      .btn-disable-gray {
        cursor: default;
        pointer-events: none;
      }
    }
  }
  .dao-item02 {
    display: flex;
    flex-direction: column;
    gap: 250px;
    padding: 200px 20px 250px 20px;
    transition: all 0.3s ease;
    @include mobile {
      gap: 150px;
      padding: 120px 20px 120px 20px;
    }
    .text-box {
      .title {
        font-size: 30px;
        color: $color-gray-500;
        @include mobile {
          font-size: 20px;
        }
        :deep(span) {
          font-size: 60px;
          font-weight: 500;
          color: $color-gray-50;
          @include mobile {
            font-size: 35px;
          }
        }
      }
      .message {
        width: 100%;
        max-width: 720px;
        margin: 0 auto;
        padding-top: 16px;
        font-size: 30px;
        line-height: 1.6em;
        color: $color-gray-400;
        word-break: break-all;
        @include mobile {
          font-size: 18px;
        }
        :deep(strong) {
          font-weight: 500;
          color: $color-gray-50;
        }
      }
    }
    .governance-box {
      .title {
        font-size: 30px;

        @include mobile {
          font-size: 20px;
        }
        :deep(span) {
          font-weight: 500;
          font-size: 60px;
          color: $color-gray-50;
          @include mobile {
            font-size: 35px;
          }
        }
      }
      .governance-step {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 10px;
        width: 100%;
        max-width: 720px;
        margin: 0 auto;
        padding-top: 50px;
        .step {
          min-width: 84px;
          padding-top: 92px;
          @include text-style($text-custom-24-bold);
          @include mobile {
            min-width: 80px;
            padding-top: 54px;
            @include text-style($text-display-bold);
          }
          &.nft {
            background: url('@/assets/images/dao/icon-digitalCard.svg?url')
              no-repeat center top;
            background-size: 84px auto;
            @include mobile {
              background-size: 42px auto;
            }
          }
          &.voting {
            background: url('@/assets/images/dao/icon-voting.svg?url') no-repeat
              center top;
            background-size: 84px auto;
            @include mobile {
              background-size: 42px auto;
            }
          }
          &.community {
            background: url('@/assets/images/dao/icon-comm.svg?url') no-repeat
              center top 7px;
            background-size: 85px auto;
            @include mobile {
              background-size: 42px auto;
            }
          }
        }
        .arrow {
          img {
            width: 20px;
          }
        }
      }
    }
  }
  .dao-item03 {
    display: flex;
    flex-direction: column;
    gap: 70px;
    padding: 100px 20px 80px 20px;
    background: url('@/assets/images/dao/dao-item03-bg.png?url') no-repeat
      center center;
    background-size: 100% 100%;
    transition: all 0.3s ease;
    @include mobile {
      gap: 40px;
      padding: 50px 20px 40px 20px;
    }
    > .title {
      font-size: 30px;
      color: $color-gray-50;
      @include mobile {
        font-size: 22px;
      }
      :deep(span) {
        font-size: 60px;
        font-weight: 500;
        color: $color-gray-50;
        @include mobile {
          font-size: 35px;
        }
      }
    }
    .creator {
      width: 100%;
      max-width: 720px;
      margin: 0 auto;
      .title {
        text-align: left;
        font-size: 30px;
        color: $color-gray-200;
        @include mobile {
          font-size: 20px;
        }
      }
    }
    .member {
      width: 100%;
      max-width: 720px;
      margin: 0 auto;
      .title {
        text-align: left;
        font-size: 30px;
        color: $color-gray-200;
        @include mobile {
          font-size: 20px;
        }
      }
    }
    .box-item {
      display: flex;
      justify-content: space-between;
      gap: 20px;
      li {
        width: 50%;
        padding: 50px 30px;
        background: linear-gradient(
          87.89deg,
          rgba(103, 127, 225, 0.1) 8.91%,
          rgba(255, 255, 255, 0.007) 183.13%
        );
        border-radius: 30px;
        border: 2px solid $color-white-5p;
        @include mobile {
          padding: 30px 5px;
          border-radius: 20px;
        }
        .dd-cont {
          display: flex;
          flex-direction: column;
          gap: 24px;
          dl {
            display: flex;
            flex-direction: column;
            gap: 24px;
            dt {
              @include text-style($text-custom-28-bold);
              color: $color-white;
              @include mobile {
                @include text-style($text-custom-18-bold);
              }
            }
            dd {
              @include text-style($text-custom-22-regular);
              padding-top: 140px;
              color: $color-gray-400;
              text-align: left;
              word-break: break-all;
              @include mobile {
                padding-top: 70px;
                @include text-style($text-body-14-regular);
              }
              &.creative {
                background: url('@/assets/images/dao/icon-creative.svg?url')
                  no-repeat center top 7px;
                background-size: 84px auto;
                @include mobile {
                  background-size: 42px auto;
                }
              }
              &.interaction {
                background: url('@/assets/images/dao/icon-interaction.svg?url')
                  no-repeat center top;
                background-size: 84px auto;
                @include mobile {
                  background-size: 38px auto;
                }
              }
              &.cooperation {
                background: url('@/assets/images/dao/icon-cooperation.svg?url')
                  no-repeat center top;
                background-size: 84px auto;
                @include mobile {
                  background-size: 42px auto;
                  background-position: center top 8px;
                }
              }
              &.communication {
                background: url('@/assets/images/dao/icon-communication.svg?url')
                  no-repeat center top;
                background-size: 84px auto;
                @include mobile {
                  background-size: 42px auto;
                }
              }
            }
          }
        }
      }
    }
  }
  .dao-item04 {
    padding: 250px 50px 200px 50px;
    transition: all 0.3s ease;
    @include mobile {
      padding: 100px 20px 100px 20px;
    }
    > .title {
      font-size: 30px;
      color: $color-gray-400;
      padding-bottom: 10px;
      @include mobile {
        font-size: 20px;
      }
      :deep(span) {
        font-size: 60px;
        font-weight: 500;
        color: $color-white;
        @include mobile {
          font-size: 35px;
        }
      }
    }
    .textBox {
      display: flex;
      flex-direction: column;
      gap: 16px;
      width: 100%;
      max-width: 660px;
      margin: 0 auto;
      @include mobile {
        gap: 8px;
      }
      li {
        dl {
          display: flex;
          justify-content: flex-start;
          flex-wrap: wrap;
          gap: 0 10px;
          dt {
            @include text-style($text-custom-32-medium);
            font-weight: 400;
            color: $color-gray-400;

            @include mobile {
              @include text-style($text-display-medium);
            }
          }
          dd {
            text-align: left;
            @include text-style($text-custom-32-medium);
            color: $color-white;
            @include mobile {
              @include text-style($text-display-medium);
            }
          }
        }
      }
    }
  }
}
</style>
